import React, { useEffect, useState } from 'react';
import { persistenceService } from '../../services/persistence';
import { useMiningDataManager } from '../../hooks/useMiningDataManager';
import { calculateTotalHashrate } from '../../utils/calculate';
import { formatLitecoinHashrate, formatSHA256Hashrate } from '../../utils';

interface ChartPanelProps {
  loading?: boolean;
  getCachedAverageHashrate?: () => any;
  getCachedCryptoProgress?: () => any;
}



const ChartPanel: React.FC<ChartPanelProps> = ({ loading = false, getCachedAverageHashrate, getCachedCryptoProgress }) => {

  const [totalHashrate, setTotalHashrate] = useState({
    Sha256TH: 0,
    dogecoinGH: 0,
    combined: 0,
    unit: 'GH/s'
  });

  const miningData = useMiningDataManager();
  const miners = miningData.miners;
  const totalHashrateString = calculateTotalHashrate(miners)

  // Load hashrate data from cache
  useEffect(() => {
    const loadHashrateData = () => {
      try {
        console.log('📦 ChartPanel: Loading hashrate data from cache...');

        // Load hashrate data
        const hashrateData = getCachedAverageHashrate ? getCachedAverageHashrate() : persistenceService.getAverageHashrate();

        console.log('✅ ChartPanel: Hashrate data:', hashrateData);

        // Calculate combined hashrate: Litecoin MH/s -> GH/s + SHA256 MH/s -> GH/s
        const litecoinGH = (hashrateData?.litecoin?.totalHashrate || 0) / 1000;
        const sha256GH = (hashrateData?.sha256?.totalHashrate || 0)/1000;
        const dogecoinGH = (hashrateData?.dogecoin?.totalHashrate || 0)/2;
        const combinedGH = ((litecoinGH + dogecoinGH) || 0)

        setTotalHashrate({
          Sha256TH: sha256GH,
          dogecoinGH: dogecoinGH,
          combined: combinedGH,
          unit: 'GH/s'
        });

      } catch (error) {
        console.error('❌ ChartPanel: Failed to load cached data:', error);
      }
    };

    loadHashrateData();
  }, [getCachedAverageHashrate]);

  if (loading) {
    return (
      <div className="space-y-4 short:space-y-2">
        <div className="card p-5 short:p-3 animate-pulse">
          <div className="h-4 bg-gray-200 dark:bg-slate-800 rounded w-1/3 mb-3"></div>
          <div className="h-9 bg-gray-200 dark:bg-slate-800 rounded w-2/3"></div>
        </div>
        <div className="card p-5 short:p-3 animate-pulse">
          <div className="h-4 bg-gray-200 dark:bg-slate-800 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-2 bg-gray-200 dark:bg-slate-800 rounded-full"></div>
            <div className="h-2 bg-gray-200 dark:bg-slate-800 rounded-full w-5/6"></div>
            <div className="h-2 bg-gray-200 dark:bg-slate-800 rounded-full w-2/3"></div>
          </div>
        </div>
      </div>
    );
  }

  // Presentation-only grouping for the per-miner bars (sorted copies; data untouched).
  // Online units lead; offline units fold into a disclosure so a large fleet
  // doesn't stretch this column out of proportion with its neighbors.
  const scryptMiners = miners
    .filter(m => m.algorithm === 'Scrypt')
    .slice()
    .sort((a, b) => b.hashrate - a.hashrate);
  const sha256Miners = miners
    .filter(m => m.algorithm === 'SHA256')
    .slice()
    .sort((a, b) => b.hashrate - a.hashrate);
  const scryptOnline = scryptMiners.filter(m => m.status === 'online');
  const scryptOffline = scryptMiners.filter(m => m.status !== 'online');
  const sha256Online = sha256Miners.filter(m => m.status === 'online');
  const sha256Offline = sha256Miners.filter(m => m.status !== 'online');
  const scryptMax = Math.max(...scryptMiners.map(m => m.hashrate), 1);
  const sha256Max = Math.max(...sha256Miners.map(m => m.hashrate), 1);

  // One dense bar row (~18px) — keeps 20+ miners readable inside a bounded region
  const renderMinerRow = (
    miner: typeof miners[number],
    barColor: string,
    max: number,
    format: (h: number) => string
  ) => (
    <div key={miner.id} className="flex items-center gap-2">
      <span className={`w-24 sm:w-28 truncate text-[11px] leading-4 ${miner.status === 'online' ? 'text-gray-600 dark:text-slate-300' : 'text-gray-400 dark:text-slate-500'}`}>
        {miner.name}
      </span>
      <div className="flex-1 h-1.5 bg-gray-100 dark:bg-slate-800 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-r-full ${miner.status === 'online' ? barColor : 'bg-gray-300 dark:bg-slate-600'}`}
          style={{ width: `${(miner.hashrate / max) * 100}%` }}
        ></div>
      </div>
      <span className={`w-20 text-right text-[11px] leading-4 font-medium tabular-nums ${miner.status === 'online' ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-slate-500'}`}>
        {format(miner.hashrate)}
      </span>
    </div>
  );

  // Collapsed-by-default disclosure for units that are not producing.
  // Native <details> — no state, no JS; page scrolling is never trapped.
  const renderOfflineGroup = (
    offline: typeof miners,
    barColor: string,
    max: number,
    format: (h: number) => string
  ) => offline.length === 0 ? null : (
    <details className="group mt-1">
      <summary className="flex items-center gap-1.5 min-h-[44px] sm:min-h-0 py-2 sm:py-1 cursor-pointer select-none list-none [&::-webkit-details-marker]:hidden text-[11px] font-medium text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300">
        <svg className="w-3 h-3 transition-transform group-open:rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        {offline.length} offline — show
      </summary>
      <div className="space-y-1 pb-1">
        {offline.map(miner => renderMinerRow(miner, barColor, max, format))}
      </div>
    </details>
  );

  return (
    <div className="space-y-4 short:space-y-2">
      {/* Hero readout — the one number this dashboard leads with */}
      <div className="card p-5 short:p-3">
        <div className="flex items-center justify-between mb-2 short:mb-1">
          <p className="stat-label">Total Combined Hashrate</p>
          <div className="flex items-center space-x-1.5">
            <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
            <span className="text-[11px] font-medium text-gray-500 dark:text-slate-400">Live</span>
          </div>
        </div>
        {/* NB: calc operators inside clamp() need surrounding whitespace —
            Tailwind writes it with underscores. Without them the declaration
            is invalid CSS and silently dropped. */}
        <p className="text-[clamp(1.5rem,1.1rem_+_1.1vw,2.25rem)] short:text-xl leading-tight font-bold tabular-nums text-red-600 dark:text-red-400">
          {totalHashrateString} + {((miningData.derivedStats.sha256Hashrate || 0)).toFixed(3)} TH/s
        </p>
        <p className="mt-1.5 text-[11px] text-gray-500 dark:text-slate-400">
          Litecoin Pool + SHA256 Pool Combined
          <span className="short:hidden tabular-nums"> · {new Date().toLocaleTimeString()} live average</span>
        </p>
      </div>

      {/* Per-miner contribution — real data, one bar per reporting miner.
          Each pool is its own scale (units differ), so groups are small multiples. */}
      <div className="card p-5 short:p-3">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 short:mb-2">
          Hashrate by Miner
        </h3>

        {miners.length === 0 ? (
          <p className="text-xs text-gray-500 dark:text-slate-400 py-4 text-center">
            No miners reporting yet
          </p>
        ) : (
          /* Bounded at every breakpoint so a 30-unit fleet cannot stretch this
             column past its neighbors; the list scrolls, the page does not.
             Group headers stay pinned inside the region for unit context. */
          <div className="max-h-[19rem] tall:max-h-[23rem] short:max-h-40 overflow-y-auto pr-1 space-y-3 short:space-y-2">
            {scryptMiners.length > 0 && (
              <div>
                <p className="flex items-center gap-1.5 stat-label mb-1.5 short:mb-1 sticky top-0 z-10 bg-white dark:bg-slate-900 py-0.5">
                  <span className="w-2 h-2 rounded-full bg-sky-600 flex-shrink-0" aria-hidden="true"></span>
                  Scrypt — scaled to pool max (GH/s)
                </p>
                <div className="space-y-1">
                  {scryptOnline.map(miner => renderMinerRow(miner, 'bg-sky-600', scryptMax, formatLitecoinHashrate))}
                </div>
                {renderOfflineGroup(scryptOffline, 'bg-sky-600', scryptMax, formatLitecoinHashrate)}
              </div>
            )}

            {sha256Miners.length > 0 && (
              <div>
                <p className="flex items-center gap-1.5 stat-label mb-1.5 short:mb-1 sticky top-0 z-10 bg-white dark:bg-slate-900 py-0.5">
                  <span className="w-2 h-2 rounded-full bg-orange-600 flex-shrink-0" aria-hidden="true"></span>
                  SHA256 — scaled to pool max (TH/s)
                </p>
                <div className="space-y-1">
                  {sha256Online.map(miner => renderMinerRow(miner, 'bg-orange-600', sha256Max, formatSHA256Hashrate))}
                </div>
                {renderOfflineGroup(sha256Offline, 'bg-orange-600', sha256Max, formatSHA256Hashrate)}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChartPanel;
